import React, { useCallback, useMemo, useState } from "react";
import { Modal } from "./modal/Modal";
import { useTranslation } from "react-i18next";
import { Flex } from "./Flex";
import { Input } from "./Input";
import * as SolidIcons from "@fortawesome/free-solid-svg-icons";
import * as RegularIcons from "@fortawesome/free-regular-svg-icons";
import { Text } from "./Text";
import { AtiraIcon } from "./AtiraIcon";
import { TestIDs } from "../tests/types/testIds";
import styled from "styled-components";
import { Spacing } from "../theme/Spacing";
import { Rounded } from "../theme/Rounded";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { CellComponentProps, Grid } from "react-window";
import isObject from "lodash/isObject";
import { AtiraTabs } from "./AtiraTabs";
import { Button } from "./Button";
import { shuffle } from "lodash";
import { GenericIcon } from "../model/shared/types/GenericIcon";

const isRealIcon = (item: IconDefinition) =>
  item &&
  isObject(item) &&
  "iconName" in item &&
  "prefix" in item &&
  "icon" in item;

const getUniqueIcons = (icons: IconDefinition[]) => {
  const map = new Map();

  icons.forEach((i) => {
    map.set(i.iconName, i);
  });

  return Array.from(map.values());
};

const allSolidIcons = getUniqueIcons(
  Object.values(SolidIcons).filter((i) =>
    isRealIcon(i as IconDefinition),
  ) as IconDefinition[],
);

const allRegularIcons = getUniqueIcons(
  Object.values(RegularIcons).filter((i) =>
    isRealIcon(i as IconDefinition),
  ) as IconDefinition[],
);

const columnCount = 5;
const rowHeight = 150;
const columnWidth = 150;

const Container = styled(Flex)`
  flex-direction: column;
  width: 100%;
  gap: ${Spacing.m};

  div[role="grid"] {
    max-height: 20rem !important;
    overflow: hidden !important;
    overflow-y: auto !important;
  }
`;

const IconBox = styled(Flex)<{ selected: boolean }>`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Spacing.m};
  border: ${({ theme }) => `2px solid ${theme.main}`};
  border-bottom-width: 3px;
  border-radius: ${Rounded.md};
  padding: ${Spacing.m};
  cursor: pointer;
  width: 75%;
  height: 75%;

  &,
  & * {
    transition: all 0.2s;
  }

  ${({ selected, theme }) =>
    selected
      ? `
    background-color: ${theme.main};
    color: ${theme.lightSub};

    p {
      color: ${theme.sub};
    }
  `
      : ""}

  p {
    user-select: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.lightMain} !important;
    color: ${({ theme }) => theme.lightSub};

    p {
      color: ${({ theme }) => theme.sub};
    }
  }
`;

const StyledTabs = styled(AtiraTabs)`
  padding-inline: 0;
  padding-top: ${Spacing.s};

  .ant-tabs-content,
  .ant-tabs-tabpane-active {
    height: 100%;
  }

  .ant-tabs-nav::before {
    display: none;
  }

  .ant-tabs-nav-list {
    width: 100% !important;
    justify-content: space-between !important;
  }

  .ant-tabs-tab {
    flex-grow: 1;
    justify-content: center;
  }
`;

type IconSelectModalProps = {
  open: boolean;
  onClose: VoidFunction;
  currentIcon?: GenericIcon;
  onChange?: (icon: GenericIcon) => void;
};

export const IconSelectModal: React.FC<IconSelectModalProps> = ({
  open = false,
  onClose,
  currentIcon,
  onChange,
}) => {
  const [keyword, setKeyword] = useState("");

  const { t } = useTranslation();

  const filteredSolidIcons = useMemo(
    () =>
      shuffle(
        allSolidIcons.filter((i) =>
          i.iconName.toLowerCase().includes(keyword.toLowerCase()),
        ),
      ),
    [keyword],
  );

  const filteredRegularIcons = useMemo(
    () =>
      shuffle(
        allRegularIcons.filter((i) =>
          i.iconName.toLowerCase().includes(keyword.toLowerCase()),
        ),
      ),
    [keyword],
  );

  const onSelectIcon = useCallback(
    (icon: IconDefinition) => {
      onChange?.({
        name: icon.iconName,
        prefix: icon.prefix,
      });
    },
    [onChange],
  );

  const renderIcon = useCallback(
    ({
      icons,
      columnIndex,
      rowIndex,
      style,
    }: CellComponentProps<{ icons: IconDefinition[] }>) => {
      const iconIndex = rowIndex * columnCount + columnIndex;
      const iconData = icons[iconIndex];

      if (!iconData) {
        return <span></span>;
      }

      return (
        <div key={iconData.iconName} style={style}>
          <IconBox
            dataTestId={TestIDs.general.iconSelectModal.icon(iconData.iconName)}
            selected={
              iconData.iconName === currentIcon?.name &&
              iconData.prefix === currentIcon?.prefix
            }
            onClick={() => onSelectIcon(iconData)}
          >
            <AtiraIcon icon={iconData} size="xl" />
            <Text align="center" fontSize="xs" color="darkerSub">
              {iconData.iconName}
            </Text>
          </IconBox>
        </div>
      );
    },
    [currentIcon, onSelectIcon],
  );

  const localOnClose = () => {
    setKeyword("");
    onClose();
  };

  return (
    <Modal
      title={t("modules.definition.basicInfo.icons.modal.title")}
      open={open}
      onClose={localOnClose}
      width={"50rem"}
    >
      <Container dataTestId={TestIDs.general.iconSelectModal.wrapper}>
        <Input
          dataTestId={TestIDs.general.iconSelectModal.search}
          placeholder={t(
            "modules.definition.basicInfo.icons.modal.input.placeholder",
          )}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />

        <StyledTabs
          items={[
            {
              key: "solid",
              label: (
                <span className={TestIDs.general.iconSelectModal.tab("solid")}>
                  {t("modules.definition.basicInfo.icons.modal.solid.title")}
                </span>
              ),
              icon: <></>,
              children: (
                <Flex flexDirection="column" gap="m">
                  <Text fontWeight={"bold"}>
                    {t("modules.definition.basicInfo.icons.modal.solid.title")}
                  </Text>

                  {Boolean(filteredSolidIcons.length) ? (
                    <Grid
                      cellComponent={renderIcon}
                      cellProps={{
                        icons: filteredSolidIcons as IconDefinition[],
                      }}
                      columnCount={columnCount}
                      columnWidth={columnWidth}
                      rowCount={Math.ceil(
                        filteredSolidIcons.length / columnCount,
                      )}
                      rowHeight={rowHeight}
                    />
                  ) : (
                    <Text align="center" color="darkerSub" fontSize="s">
                      {t(
                        "modules.definition.basicInfo.icons.modal.icons.empty",
                      )}
                    </Text>
                  )}
                </Flex>
              ),
            },
            {
              key: "regular",
              label: (
                <span
                  className={TestIDs.general.iconSelectModal.tab("regular")}
                >
                  {t("modules.definition.basicInfo.icons.modal.regular.title")}
                </span>
              ),
              icon: <></>,
              children: (
                <Flex flexDirection="column" gap="m">
                  <Text fontWeight={"bold"}>
                    {t(
                      "modules.definition.basicInfo.icons.modal.regular.title",
                    )}
                  </Text>

                  {Boolean(filteredRegularIcons.length) ? (
                    <Grid
                      cellComponent={renderIcon}
                      cellProps={{
                        icons: filteredRegularIcons as IconDefinition[],
                      }}
                      columnCount={columnCount}
                      columnWidth={columnWidth}
                      rowCount={Math.ceil(
                        filteredRegularIcons.length / columnCount,
                      )}
                      rowHeight={rowHeight}
                    />
                  ) : (
                    <Text align="center" color="darkerSub" fontSize="s">
                      {t(
                        "modules.definition.basicInfo.icons.modal.icons.empty",
                      )}
                    </Text>
                  )}
                </Flex>
              ),
            },
          ]}
        />

        <Button
          onClick={localOnClose}
          width="20rem"
          hover
          margin="auto"
          dataTestId={TestIDs.general.iconSelectModal.save}
        >
          {t("common.save")}
        </Button>
      </Container>
    </Modal>
  );
};
